import React, { useState, useEffect, useRef } from 'react';
import {
  sendMessage,
  getConversation,
  subscribeToConversation,
  markMessagesAsRead,
  getParentConversations
} from '../../services/parentChildMessagingService';
import './ParentChildMessaging.css';

const ParentChildMessaging = ({ parentId, selectedChildId, selectedChildName }) => {
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const conversationUnsubscribeRef = useRef(null);

  useEffect(() => {
    if (parentId) {
      loadConversations();
    }
  }, [parentId]);

  useEffect(() => {
    if (selectedChildId) {
      loadMessages();
      markAsRead();
    } else {
      setMessages([]);
    }

    return () => {
      if (conversationUnsubscribeRef.current) {
        conversationUnsubscribeRef.current();
      }
    };
  }, [selectedChildId, parentId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const convs = await getParentConversations(parentId);
      setConversations(convs);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      const msgs = await getConversation(parentId, selectedChildId);
      setMessages(msgs);

      // Subscribe to real-time updates
      if (conversationUnsubscribeRef.current) {
        conversationUnsubscribeRef.current();
      }

      conversationUnsubscribeRef.current = subscribeToConversation(
        parentId,
        selectedChildId,
        (updatedMessages) => {
          setMessages(updatedMessages);
          markAsRead();
        }
      );
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const markAsRead = async () => {
    if (selectedChildId) {
      try {
        await markMessagesAsRead(parentId, selectedChildId, parentId);
      } catch (error) {
        console.error('Error marking messages as read:', error);
      }
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) {
      return;
    }

    setSending(true);
    try {
      await sendMessage(parentId, selectedChildId, newMessage.trim(), 'parent');
      setNewMessage('');
      await loadConversations(); // Refresh conversation list
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  if (!selectedChildId) {
    return (
      <div className="messaging-empty">
        <div className="empty-icon">💬</div>
        <p>Select a child to start messaging</p>
      </div>
    );
  }

  return (
    <div className="parent-messaging">
      <div className="messaging-header">
        <h3>💬 Messages with {selectedChildName}</h3>
      </div>

      <div className="messages-container" ref={messagesEndRef}>
        {messages.length === 0 ? (
          <div className="no-messages">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((message) => {
            const isParent = message.senderId === parentId;
            return (
              <div
                key={message.id}
                className={`message ${isParent ? 'message-sent' : 'message-received'}`}
              >
                <div className="message-content">
                  <p>{message.message}</p>
                  <span className="message-time">{formatTime(message.createdAt)}</span>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <form className="message-input-form" onSubmit={handleSendMessage}>
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type your message..."
          className="message-input"
          disabled={sending}
        />
        <button
          type="submit"
          className="send-button"
          disabled={!newMessage.trim() || sending}
        >
          {sending ? 'Sending...' : 'Send'}
        </button>
      </form>
    </div>
  );
};

export default ParentChildMessaging;

